import React, { useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { I18nReactContext } from '../../../../../contexts/I18nContext';
import { Modal } from '../../../../Smart/Modal/Modal';
import { Button } from '../../../../Form/Button/Button';
import { buttonColors } from '../../../../Pickers/InputTheme';
import { v4 as uuidv4 } from 'uuid';
import FormatTextdirectionLToR from '@mui/icons-material/FormatTextdirectionLToR';
import FormatTextdirectionRToL from '@mui/icons-material/FormatTextdirectionRToL';
import './CourseTemplateBank.scss';
import { templates } from '../../../lib/templates';
import { TemplateList } from './TemplateList';
import { TemplateItem } from './TemplateItem';
import { EditorContent } from './EditorContent';
import { TextComponent } from './TextComponent';
import { BulletsComponent } from './BulletsComponent';
import { LogoComponent } from './LogoComponent';
import { ImageComponent } from './ImageComponent';
import { VideoComponent } from './VideoComponent';
import { ShapeComponent } from './ShapeComponent';
import { generateCourseWithTemplateAPI, checkCourseAIGenerationStatusAPI, getPublicTemplatesForCourseGenerationAPI } from '../../../../../Services/api';

/**
 * Simplified PreviewComponent for rendering components in preview mode without drag functionality
 */
const PreviewComponent = ({ id, component, templateId }) => {
  const style = {
    position: 'absolute',
    left: `${component.x || 0}px`,
    top: `${component.y || 0}px`,
    width: component.width || 200,
    height: component.height === 'auto' ? 'auto' : (component.height || 150),
    zIndex: 1,
    pointerEvents: 'none', // Disable all interactions for preview
  };

  return (
    <div className="preview-component" style={style}>
      {component.type === 'text' && (
        <TextComponent 
          id={id}
          content={component.content}
          onContentChange={() => {}}
          isSelected={false}
          onSelect={() => {}}
          fontSize={component.fontSize}
          fontWeight={component.bold ? 'bold' : 'normal'}
          fontStyle={component.italic ? 'italic' : 'normal'}
          textDecoration={component.underline ? 'underline' : 'none'}
          color={component.color}
          borderRadius={component.borderRadius}
          backgroundColor={component.backgroundColor}
          opacity={component.opacity || 1}
          direction={component.direction || 'rtl'}
          mode="view"
          onAnimationComplete={() => {}}
          isSequentialAnimationActive={false}
        />
      )}
      {component.type === 'bullets' && (
        <BulletsComponent 
          id={id}
          content={component.content}
          onContentChange={() => {}}
          isSelected={false}
          onSelect={() => {}}
          fontSize={component.fontSize}
          fontWeight={component.bold ? 'bold' : 'normal'}
          fontStyle={component.italic ? 'italic' : 'normal'}
          textDecoration={component.underline ? 'underline' : 'none'}
          color={component.color}
          borderRadius={component.borderRadius}
          backgroundColor={component.backgroundColor}
          opacity={component.opacity || 1}
          direction={component.direction || 'rtl'}
          mode="view"
          onAnimationComplete={() => {}}
          isSequentialAnimationActive={false}
        />
      )}
      {component.type === 'logo' && (
        <LogoComponent 
          id={id}
          isSelected={false}
          onSelect={() => {}}
          borderRadius={component.borderRadius}
          backgroundColor={component.backgroundColor}
          opacity={component.opacity || 1}
          mode="view"
          onAnimationComplete={() => {}}
          isSequentialAnimationActive={false}
        />
      )}
      {component.type === 'image' && (
        <ImageComponent 
          id={id}
          content={component.content}
          isSelected={false}
          onSelect={() => {}}
          onContentChange={() => {}}
          borderRadius={component.borderRadius}
          backgroundColor={component.backgroundColor}
          opacity={component.opacity || 1}
          mode="view"
          signDocId={null}
          sectionId={null}
          onCourseUpdate={() => {}}
          isSuperAdminMode={true}
          templateId={templateId}
          onAnimationComplete={() => {}}
          isSequentialAnimationActive={false}
        />
      )}
      {component.type === 'video' && (
        <VideoComponent 
          id={id}
          content={component.content}
          isSelected={false}
          onSelect={() => {}}
          onContentChange={() => {}}
          borderRadius={component.borderRadius}
          backgroundColor={component.backgroundColor}
          opacity={component.opacity || 1}
          mode="view"
          signDocId={null}
          sectionId={null}
          onCourseUpdate={() => {}}
          onVideoComplete={() => {}}
          courseCompleted={false}
          isSuperAdminMode={true}
          templateId={templateId}
          onAnimationComplete={() => {}}
          isSequentialAnimationActive={false}
        />
      )}
      {component.type === 'shape' && (
        <ShapeComponent 
          id={id}
          content={component.content}
          onContentChange={() => {}}
          isSelected={false}
          onSelect={() => {}}
          borderRadius={component.borderRadius}
          backgroundColor={component.backgroundColor || '#000000'}
          opacity={component.opacity || 1}
          mode="view"
          shapeType={component.shapeType || 'rectangle'}
          borderColor={component.borderColor || '#000000'}
          borderWidth={component.borderWidth || 0}
          onAnimationComplete={() => {}}
          isSequentialAnimationActive={false}
        />
      )}
    </div>
  );
};

/**
 * EditorContentPreview component to render a real preview of a chapter using EditorContent
 * Uses the actual EditorContent component in view mode with scaling
 */
const EditorContentPreview = React.memo(({ chapter, i18n, templateId }) => {
  if (!chapter) {
    return (
      <div className="canvas-preview empty">
        <div className="empty-canvas-message">
          {i18n.translate('weezbeeCourse.courseTemplateBank.noPreviewAvailable')}
        </div>
      </div>
    );
  }

  if (!chapter.components || !Array.isArray(chapter.components) || chapter.components.length === 0) {
    // Show a placeholder preview for templates without components
    return (
      <div className="canvas-preview placeholder">
        <div className="placeholder-content">
          <div className="placeholder-header"></div>
          <div className="placeholder-text-lines">
            <div className="placeholder-line"></div>
            <div className="placeholder-line short"></div>
          </div>
          <div className="placeholder-image"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="canvas-preview editor-content-preview">
      <div className="preview-container">
        <EditorContent
          chapterData={chapter}
          components={chapter.components}
          selectedComponentId={null}
          mode="view"
          signDocId={null}
          onVideoComplete={() => {}}
          courseCompleted={false}
          isFullScreen={false}
          toggleFullscreen={() => {}}
          isSuperAdminMode={true}
          templateId={templateId}
          isComponentBankCollapsed={true}
          toggleComponentBank={() => {}}
          handleDragStart={() => {}}
          handleDragEnd={() => {}}
          handleCanvasClick={() => {}}
          handleCanvasDoubleClick={() => {}}
          handleSelectComponent={() => {}}
          handleContentChange={() => {}}
          handleRemoveComponent={() => {}}
          handleResize={() => {}}
          handleOpenEditorDrawer={() => {}}
          handleAddComponent={() => {}}
          onUpdate={() => {}}
          canvasWrapperRef={{ current: null }}
          componentsContainerRef={{ current: null }}
          showComponentAnimations={false}
          isSequentialAnimationActive={false}
          currentAnimatingComponentIndex={0}
          animatedComponents={new Set()}
          handleAnimationComplete={() => {}}
          DraggableComponent={PreviewComponent}
          isPreview={true}
        />
      </div>
    </div>
  );
});

EditorContentPreview.displayName = 'EditorContentPreview';

/**
 * CourseTemplateBank component displays a modal with available course templates for creating new courses with AI.
 * Users can select a course template to use as a starting point for their course content.
 * When a course template is selected, a side panel displays the chapter templates included in the course.
 *
 * @component
 * @param {Object} props - Component props
 * @param {boolean} [props.open=true] - Whether the modal is open
 * @param {Function} props.onSelectCourseTemplate - Callback function called when a course template is selected, receives the new course object
 * @param {Function} props.onClose - Callback function called when the template bank is closed without selecting a template
 * @param {string} props.description - The description entered by the user in the AICourseGenRow component
 * @param {string} props.signDocId - The ID of the sign document to associate with the course
 * @param {boolean} [props.embeddedMode=false] - When true, hides header close button and footer buttons (for use in wizard)
 * @param {Function} [props.onTemplateSelect] - Callback when template is selected in embedded mode (receives template object)
 * @returns {JSX.Element} The rendered CourseTemplateBank component
 */
export const CourseTemplateBank = ({
  open = true,
  onSelectCourseTemplate,
  onClose,
  description,
  signDocId: propSignDocId,
  embeddedMode = false,
  onTemplateSelect
}) => {
  // Get i18n context for translations and RTL status
  const { i18n, isRtl } = useContext(I18nReactContext);
  // State for the selected direction (default based on user language)
  const [direction, setDirection] = useState(isRtl ? 'rtl' : 'ltr');
  // State for the selected course template
  const [selectedCourseTemplate, setSelectedCourseTemplate] = useState(null);
  // State for managing tabs
  const [activeTab, setActiveTab] = useState('default');
  // State for designed templates
  const [designedTemplates, setDesignedTemplates] = useState([]);
  const [loadingDesignedTemplates, setLoadingDesignedTemplates] = useState(false);
  // No longer need state for tracking AI generation as it's handled by the parent component

  // Fetch designed templates when component mounts
  useEffect(() => {
    const fetchDesignedTemplates = async () => {
      setLoadingDesignedTemplates(true);
      try {
        const templates = await getPublicTemplatesForCourseGenerationAPI();
        setDesignedTemplates(templates || []);
      } catch (error) {
        console.error('Error fetching designed templates:', error);
        setDesignedTemplates([]);
      } finally {
        setLoadingDesignedTemplates(false);
      }
    };

    fetchDesignedTemplates();
  }, []);

  /**
   * Helper function to get the first chapter from a designed template
   * In our system, sections and chapters are the same thing - section is how chapter is called in the response
   * @param {Object} template - The designed template object
   * @returns {Object|null} - The first section (which is the first chapter) or null if not found
   */
  const getFirstChapter = useCallback((template) => {
    if (!template.sections || !Array.isArray(template.sections) || template.sections.length === 0) {
      return null;
    }

    // Return the first section directly since sections and chapters are the same thing
    return template.sections[0];
  }, []);

  /**
   * Helper function to count total chapters in a designed template
   * In our system, sections and chapters are the same thing - section is how chapter is called in the response
   * @param {Object} template - The designed template object
   * @returns {number} - Total number of chapters (which is the number of sections)
   */
  const getTotalChapters = useCallback((template) => {
    if (!template.sections || !Array.isArray(template.sections)) return 0;

    // Return the number of sections directly since sections and chapters are the same thing
    return template.sections.length;
  }, []);

  // Course templates data - 6 different course templates with varying numbers of chapters
  // Each course template has a different subset and order of chapter templates
  const courseTemplates = [
    {
      id: 'business-course',
      nameKey: 'weezbeeCourse.courseTemplateBank.templates.businessCourse.name',
      descriptionKey: 'weezbeeCourse.courseTemplateBank.templates.businessCourse.description',
      thumbnail: 'standard-slide-thumbnail',
      courseType: 'weezbeeCourse.courseTemplateBank.courseTypes.business',
      chapterCount: 15,
      chapters: [
        // Business courses focus on professional slides, bullet points, and text-based content
        templates[0], templates[2], templates[4], templates[7], templates[1], 
        templates[3], templates[5], templates[8], templates[6], templates[9], 
        templates[2], templates[0], templates[4], templates[7], templates[10]
      ]
    },
    {
      id: 'technical-course',
      nameKey: 'weezbeeCourse.courseTemplateBank.templates.technicalCourse.name',
      descriptionKey: 'weezbeeCourse.courseTemplateBank.templates.technicalCourse.description',
      thumbnail: 'video-focused-thumbnail',
      courseType: 'weezbeeCourse.courseTemplateBank.courseTypes.technical',
      chapterCount: 20,
      chapters: [
        // Technical courses emphasize video content and bullet points
        templates[1], templates[5], templates[9], templates[2], templates[0], 
        templates[3], templates[7], templates[4], templates[6], templates[8], 
        templates[1], templates[5], templates[9], templates[2], templates[0],
        templates[3], templates[7], templates[10], templates[4], templates[6]
      ]
    },
    {
      id: 'educational-course',
      nameKey: 'weezbeeCourse.courseTemplateBank.templates.educationalCourse.name',
      descriptionKey: 'weezbeeCourse.courseTemplateBank.templates.educationalCourse.description',
      thumbnail: 'bullet-points-thumbnail',
      courseType: 'weezbeeCourse.courseTemplateBank.courseTypes.educational',
      chapterCount: 25,
      chapters: [
        // Educational courses have a mix of all template types with emphasis on text and bullets
        templates[2], templates[4], templates[7], templates[0], templates[3], 
        templates[1], templates[5], templates[9], templates[6], templates[8], 
        templates[2], templates[4], templates[7], templates[0], templates[3],
        templates[1], templates[5], templates[9], templates[6], templates[8],
        templates[10], templates[2], templates[4], templates[0], templates[3]
      ]
    },
    {
      id: 'onboarding-course',
      nameKey: 'weezbeeCourse.courseTemplateBank.templates.onboardingCourse.name',
      descriptionKey: 'weezbeeCourse.courseTemplateBank.templates.onboardingCourse.description',
      thumbnail: 'centered-image-thumbnail',
      courseType: 'weezbeeCourse.courseTemplateBank.courseTypes.onboarding',
      chapterCount: 10,
      chapters: [
        // Onboarding courses focus on images and simple layouts
        templates[3], templates[6], templates[8], templates[0], templates[10], 
        templates[2], templates[4], templates[7], templates[1], templates[5]
      ]
    },
    {
      id: 'compliance-course',
      nameKey: 'weezbeeCourse.courseTemplateBank.templates.complianceCourse.name',
      descriptionKey: 'weezbeeCourse.courseTemplateBank.templates.complianceCourse.description',
      thumbnail: 'text-and-bullets-thumbnail',
      courseType: 'weezbeeCourse.courseTemplateBank.courseTypes.compliance',
      chapterCount: 12,
      chapters: [
        // Compliance courses emphasize text and bullet points for clear information
        templates[2], templates[4], templates[7], templates[0], templates[10], 
        templates[2], templates[4], templates[7], templates[0], templates[3],
        templates[2], templates[4]
      ]
    },
    {
      id: 'product-course',
      nameKey: 'weezbeeCourse.courseTemplateBank.templates.productCourse.name',
      descriptionKey: 'weezbeeCourse.courseTemplateBank.templates.productCourse.description',
      thumbnail: 'image-gallery-thumbnail',
      courseType: 'weezbeeCourse.courseTemplateBank.courseTypes.product',
      chapterCount: 18,
      chapters: [
        // Product courses focus on images and galleries
        templates[8], templates[3], templates[6], templates[0], templates[10], 
        templates[8], templates[3], templates[6], templates[1], templates[5],
        templates[8], templates[3], templates[6], templates[2], templates[4],
        templates[8], templates[3], templates[0]
      ]
    }
  ];

  /**
   * Handles the selection of a course template
   * In embedded mode, also notifies parent via onTemplateSelect callback
   *
   * @param {Object} courseTemplate - The selected course template object
   */
  const handleSelectCourseTemplate = (courseTemplate) => {
    setSelectedCourseTemplate(courseTemplate);
    // In embedded mode, notify parent about template selection
    if (embeddedMode && onTemplateSelect) {
      onTemplateSelect(courseTemplate);
    }
  };

  /**
   * Handles the selection of a direction (LTR or RTL)
   * 
   * @param {string} newDirection - The selected direction ('ltr' or 'rtl')
   */
  const handleDirectionChange = (newDirection) => {
    setDirection(newDirection);
  };

  // Polling has been removed from this component as it will be handled by the parent component

  /**
   * Creates a new course based on the selected course template
   */
  const handleCreateCourse = async () => {
    if (!selectedCourseTemplate || !propSignDocId) return;

    let newCourse;

    // Handle designed templates differently from default templates
    if (activeTab === 'designed') {
      // For designed templates, use the sections from the template
      newCourse = {
        id: propSignDocId,
        title: selectedCourseTemplate.templateName || i18n.translate('weezbeeCourse.courseTemplateBank.defaultCourseTitle'),
        description: description || selectedCourseTemplate.description || i18n.translate('weezbeeCourse.courseTemplateBank.defaultCourseDescription'),
        sections: selectedCourseTemplate.sections || []
      };
    } else {
      // For default templates, use the existing logic
      newCourse = {
        id: propSignDocId, // Use the signDocId passed from props
        title: i18n.translate('weezbeeCourse.courseTemplateBank.defaultCourseTitle'),
        // Use the user's description if provided, otherwise use the default
        description: description || i18n.translate('weezbeeCourse.courseTemplateBank.defaultCourseDescription'),
        sections: [
          {
            id: uuidv4(),
            title: i18n.translate('weezbeeCourse.courseTemplateBank.defaultSectionTitle'),
            chapters: selectedCourseTemplate.chapters.map(template => ({
              id: uuidv4(),
              title: i18n.translate('weezbeeCourse.editor.title.defaultValue'),
              description: i18n.translate('weezbeeCourse.editor.description.defaultValue'),
              template: 'advanced',
              components: template.getComponents(i18n, direction),
              backgroundImage: template.backgroundImage
            }))
          }
        ]
      };
    }

    try {
      // Call the API to generate the course with AI using the template
      const response = await generateCourseWithTemplateAPI({
        signDocId: propSignDocId, // Use the signDocId passed from props
        description: newCourse.description,
        courseTemplate: newCourse
      });

      // Pass the signDocId and template info to the parent component for polling
      onSelectCourseTemplate({ 
        signDocId: propSignDocId,
        templateInfo: {
          totalChapters: activeTab === 'designed' 
            ? (selectedCourseTemplate.sections ? selectedCourseTemplate.sections.length : 0)
            : (selectedCourseTemplate.chapters ? selectedCourseTemplate.chapters.length : 0),
          description: newCourse.description
        }
      });

      // Close the modal immediately
      onClose();
    } catch (error) {
      console.error('Error starting course generation:', error);

      // Show error message
      alert(i18n.translate('weezbeeCourse.courseTemplateBank.generationStartError'));
    }
  };

  const content = (
    <div className="CourseTemplateBank">
      <div className="course-template-content">
        {/* Course templates section with tabs */}
        <div className="course-templates-section">
          <h3>{i18n.translate('weezbeeCourse.courseTemplateBank.courseTemplatesTitle')}</h3>

          {/* Template tabs with direction control on the same row */}
          <div className="template-tabs-row">
            <div className="template-tabs">
              <button
                className={`tab-button ${activeTab === 'default' ? 'active' : ''}`}
                onClick={() => setActiveTab('default')}
              >
                {i18n.translate('weezbeeCourse.courseTemplateBank.defaultTemplatesTab')}
              </button>
              <button
                className={`tab-button ${activeTab === 'designed' ? 'active' : ''}`}
                onClick={() => setActiveTab('designed')}
              >
                {i18n.translate('weezbeeCourse.courseTemplateBank.designedTemplatesTab')}
              </button>
            </div>

            {/* Direction selection - on the opposite side of tabs */}
            <div className="direction-control">
              <span className="direction-label">{i18n.translate('weezbeeCourse.templateBank.directionSelection')}</span>
              <div className="direction-buttons">
                <button
                  className={`direction-button ${direction === 'rtl' ? 'active' : ''}`}
                  onClick={() => handleDirectionChange('rtl')}
                  aria-label={i18n.translate('weezbeeCourse.templateBank.rtlDirection')}
                  title={i18n.translate('weezbeeCourse.templateBank.rtlDirection')}
                >
                  <FormatTextdirectionRToL />
                </button>
                <button
                  className={`direction-button ${direction === 'ltr' ? 'active' : ''}`}
                  onClick={() => handleDirectionChange('ltr')}
                  aria-label={i18n.translate('weezbeeCourse.templateBank.ltrDirection')}
                  title={i18n.translate('weezbeeCourse.templateBank.ltrDirection')}
                >
                  <FormatTextdirectionLToR />
                </button>
              </div>
            </div>
          </div>

          {/* Default templates tab content */}
          {activeTab === 'default' && (
            <div className="course-templates-grid">
              {courseTemplates.map(courseTemplate => (
                <div 
                  key={courseTemplate.id}
                  className={`course-template-card ${selectedCourseTemplate?.id === courseTemplate.id ? 'selected' : ''}`}
                  onClick={() => handleSelectCourseTemplate(courseTemplate)}
                >
                  <div className="template-info">
                    <h4>{i18n.translate(courseTemplate.nameKey)}</h4>
                    <div className="template-meta">
                      <span className="course-type">{i18n.translate(courseTemplate.courseType)}</span>
                      <span className="chapter-count">
                        {i18n.translate('weezbeeCourse.courseTemplateBank.slideCount', { 
                          context: { count: courseTemplate.chapterCount } 
                        })}
                      </span>
                    </div>
                    <p>{i18n.translate(courseTemplate.descriptionKey)}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Designed templates tab content */}
          {activeTab === 'designed' && (
            <div className="designed-templates-content">
              {loadingDesignedTemplates ? (
                <div className="loading-message">
                  {i18n.translate('weezbeeCourse.courseTemplateBank.loadingTemplates')}
                </div>
              ) : designedTemplates.length === 0 ? (
                <div className="no-templates-message">
                  {i18n.translate('weezbeeCourse.courseTemplateBank.noDesignedTemplates')}
                </div>
              ) : (
                <div className="course-templates-grid">
                  {designedTemplates.map(template => {
                    const firstChapter = getFirstChapter(template);
                    const totalChapters = getTotalChapters(template);

                    return (
                      <div 
                        key={template.id}
                        className={`course-template-card designed-template styled-template ${selectedCourseTemplate?.id === template.id ? 'selected' : ''}`}
                        onClick={() => handleSelectCourseTemplate(template)}
                      >
                        {/* Top section with title, description, and chapter count */}
                        <div className="template-header">
                          <h4>{template.templateName}</h4>
                          <p className="template-description">
                            {template.description || i18n.translate('weezbeeCourse.courseTemplateBank.noDescription')}
                          </p>
                          <div className="template-stats">
                            <span className="chapter-count">
                              {i18n.translate('weezbeeCourse.courseTemplateBank.slideCount', { 
                                context: { count: totalChapters } 
                              })}
                            </span>
                            <span className="template-subject">{template.templateSubject}</span>
                          </div>
                        </div>

                        {/* Bottom section with canvas preview */}
                        <div className="template-preview">
                          <EditorContentPreview chapter={firstChapter} i18n={i18n} templateId={template.id} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Chapter templates section - only shown when a default course template is selected */}
        {selectedCourseTemplate && activeTab === 'default' && selectedCourseTemplate.chapters && (
          <div className="chapter-templates-section">
            <h3>
              {i18n.translate('weezbeeCourse.courseTemplateBank.chapterTemplatesTitle', { 
                context: { count: selectedCourseTemplate.chapterCount || selectedCourseTemplate.chapters.length } 
              })}
            </h3>

            <div className="chapter-templates-list">
              {selectedCourseTemplate.chapters.map((template, index) => (
                <div key={`${selectedCourseTemplate.id}-chapter-${index}`} className="chapter-template-item">
                  <TemplateItem 
                    template={template}
                    direction={direction}
                    onSelect={() => {}} // No action needed when clicking on chapter templates
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Generation progress section has been removed as it's now handled by the parent component */}
    </div>
  );

  // If embedded mode, return content without Modal wrapper, title, and footer
  if (embeddedMode) {
    return content;
  }

  // Otherwise, wrap with Modal and add title and footer using standard modal structure
  return (
    <Modal open={open} onClose={onClose}>
      <div className="CourseTemplateBankModal modalWrapper">
        {/* Standard modal title */}
        <div className="modalTitle">
          <div>{i18n.translate('weezbeeCourse.courseTemplateBank.title')}</div>
        </div>

        {/* Modal content */}
        <div className="modalContent">
          {content}
        </div>

        {/* Footer with action buttons - only in standalone mode */}
        <div className="modalFooter">
          <Button
            color={buttonColors.GREY}
            onClick={onClose}
            text={i18n.translate('weezbeeCourse.courseTemplateBank.cancelButton')}
            dataTestId="template-bank-cancel-button"
          />

          <Button
            color={buttonColors.YELLOW}
            onClick={handleCreateCourse}
            disabled={!selectedCourseTemplate}
            text={i18n.translate('weezbeeCourse.courseTemplateBank.createButton')}
            dataTestId="template-bank-create-button"
          />
        </div>
      </div>
    </Modal>
  );
};
